import React, { useEffect, useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Icons from "react-feather";
import { Loading, MultiSelect } from "components/form";
import {
  axiosInstance,
  handleErrors,
  handleSuccessMessages,
} from "store/auth";

const toOption = (user) => ({
  value: Number(user.id),
  label: `${user.full_name || user.user_name || `کاربر #${user.id}`} - ${
    user.user_name || `#${user.id}`
  }`,
});

const loadAllCatalogUsers = async () => {
  const users = [];
  let page = 1;
  let lastPage = 1;

  do {
    const result = await axiosInstance.get(
      `/panel/catalog-users?perpage=100&page=${page}`,
    );
    users.push(...(result.data?.data || []));
    lastPage = Number(result.data?.meta?.last_page || 1);
    page += 1;
  } while (page <= lastPage);

  return users;
};

export default function ProductCatalogAccess({ productId }) {
  const [loading, setLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!productId) {
      return;
    }

    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const [catalogUsers, accessResult] = await Promise.all([
          loadAllCatalogUsers(),
          axiosInstance.get(`/panel/products/${productId}/catalog-users`),
        ]);

        if (!mounted) {
          return;
        }

        setAvailableUsers(catalogUsers);
        setAssignedUsers(accessResult.data?.users || []);
        setIsVisible(true);
      } catch (error) {
        if (error?.response?.status === 403) {
          setIsVisible(false);
        } else {
          handleErrors(error);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [productId]);

  const options = useMemo(() => {
    const merged = [...assignedUsers, ...availableUsers];
    const uniqueUsers = new Map();

    merged.forEach((user) => {
      if (user?.id && !uniqueUsers.has(Number(user.id))) {
        uniqueUsers.set(Number(user.id), toOption(user));
      }
    });

    return Array.from(uniqueUsers.values());
  }, [assignedUsers, availableUsers]);

  if (!isVisible) {
    return null;
  }

  if (!productId) {
    return (
      <div className="mt-8 rounded-lg border border-gray-200 bg-gray-50 p-5 dark:border-dark-5 dark:bg-dark-2">
        <div className="flex items-start">
          <div className="flex-shrink-0 rounded-full bg-theme-1 bg-opacity-10 p-3 text-theme-1">
            <Icons.Users className="h-5 w-5" />
          </div>
          <div className="mr-3">
            <h3 className="text-base font-medium">دسترسی مستقیم کاتالوگ</h3>
            <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
              برای انتخاب کاربران کاتالوگ، ابتدا اطلاعات اولیه محصول را ذخیره
              کنید. پس از ذخیره، این بخش به‌صورت خودکار فعال می‌شود.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-5 dark:bg-dark-2">
      <div className="flex flex-col border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-dark-5">
        <div className="flex items-start">
          <div className="flex-shrink-0 rounded-full bg-theme-1 bg-opacity-10 p-3 text-theme-1">
            <Icons.Users className="h-5 w-5" />
          </div>
          <div className="mr-3">
            <h3 className="text-base font-medium">دسترسی مستقیم کاتالوگ</h3>
            <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
              این محصول را به کاتالوگ یک یا چند کاربر اضافه کنید یا دسترسی آن‌ها
              را بردارید.
            </p>
          </div>
        </div>
        {!loading && (
          <div className="mt-3 flex-shrink-0 self-start rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 sm:mt-0 sm:self-auto dark:border-dark-5 dark:bg-dark-3 dark:text-gray-400">
            {assignedUsers.length} کاربر دارای دسترسی
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loading />
        </div>
      ) : (
        <Formik
          initialValues={{
            user_ids: assignedUsers.map((user) => Number(user.id)),
          }}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const result = await axiosInstance.put(
                `/panel/products/${productId}/catalog-users`,
                { user_ids: values.user_ids || [] },
              );
              setAssignedUsers(result.data?.users || []);
              handleSuccessMessages("دسترسی مستقیم کاتالوگ ذخیره شد");
            } catch (error) {
              handleErrors(error);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, values }) => (
            <Form className="p-5">
              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-5 dark:bg-dark-3">
                <div>
                  <Field
                    label="کاربران کاتالوگ"
                    name="user_ids"
                    valueOnly
                    component={MultiSelect}
                    options={options}
                  />
                  <div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <Icons.Info className="ml-1 h-4 w-4 flex-shrink-0" />
                    برای حذف دسترسی، کاربر را از فهرست انتخاب‌شده بردارید.
                  </div>
                </div>

                <div className="mt-5 flex flex-col border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between dark:border-dark-5">
                  <div className="mb-3 text-sm text-gray-600 sm:mb-0 dark:text-gray-400">
                    تعداد انتخاب‌شده:
                    <span className="mr-1 font-medium text-theme-1">
                      {values.user_ids?.length || 0} کاربر
                    </span>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary flex w-full items-center justify-center sm:w-48"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loading />
                    ) : (
                      <>
                        <Icons.Save className="ml-2 h-4 w-4" />
                        ذخیره دسترسی
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}

